<template>
  <div class="analytic-review">
    <header class="analytic-review__header">
      <div class="analytic-review__header__title" v-if="chart.chart_review.title">
        {{ chart.chart_review.title[$i18n.locale] || chart.chart_review.title.default }}
      </div>
      <div
        class="analytic-distribution__header__tooltip backoffice-icons"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover click',
          content: chart.chart_review.description[$i18n.locale] || chart.chart_review.description.default,
          offset: 3,
        }"
        v-if="chart.chart_review.description"
      >
        info
      </div>
    </header>
    <div class="analytic-review__legend" v-if="chart.chart_review.legend">
      <ui-legend :legends="chart.chart_review.legend" />
    </div>
    <div class="analytic-review__container">
      <ui-chart class="analytic-review__container__chart" id="chart-review" :options="options" />
    </div>
  </div>
</template>

<script>
import UiLegend from '@/components/UI/Legend.vue'
import UiChart from '@/components/UI/Chart.vue'

const chartFontFamily = 'OpenSans'
const chartFontSize = 11

export default {
  name: 'AnalyticReview',
  components: {
    UiChart,
    UiLegend,
  },
  props: {
    chart: {
      type: Object,
      required: true,
    },
  },
  watch: {
    chart: {
      deep: true,
      handler() {
        if (this.chart.chart_review) {
          this.generateSeries()
        }
      },
    },
  },
  data() {
    return {
      options: {
        tooltip: {
          confine: true,
          textStyle: {
            fontFamily: chartFontFamily,
            lineHeight: 1.15,
          },
          backgroundColor: 'rgba(71, 73, 77, 0.8)',
          padding: 16,
          extraCssText: 'border-radius: 10px;',
        },
        grid: {
          top: '16px',
          left: '28px',
          right: '16px',
          bottom: '38px',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
            color: '#47494D',
          },
          axisLabel: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
          },
        },
        yAxis: {
          nameLocation: 'middle',
          nameGap: 30,
          nameRotation: 90,
          nameTextStyle: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
            color: '#47494D',
          },
          axisLabel: {
            fontFamily: chartFontFamily,
            fontSize: chartFontSize,
          },
          scale: true,
        },
        series: [],
      },
    }
  },
  mounted() {
    if (this.chart.chart_review) {
      this.generateSeries()
    }
  },
  methods: {
    generateSeries() {
      let series = []

      this.chart.chart_review.data.forEach(d => {
        let data = []
        data.push([d.reviews_count, d.reviews_score, d.locations_count, d.name, d.acronym, d.color, d.circle_size])

        series.push({
          name: d.name,
          type: 'scatter',
          data: data,
          symbolSize: params => params[6],
          label: {
            show: true,
            formatter: params => params.data[4],
            position: 'inside',
            textShadowColor: '#000',
            textShadowBlur: 1,
            color: '#fff',
            textBorderWidth: 1,
            textBorderColor: '#000',
          },
          itemStyle: {
            color: params => params.data[5],
            opacity: 0.8,
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowOffsetY: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        })
      })

      this.options.yAxis.name =
        this.chart.chart_review.xAxis.name[this.$i18n.locale] || this.chart.chart_review.xAxis.name.default
      this.options.xAxis.name =
        this.chart.chart_review.yAxis.name[this.$i18n.locale] || this.chart.chart_review.yAxis.name.default
      this.options.yAxis.min = 1
      this.options.yAxis.max = 5
      this.options.series = series
      this.options.tooltip.formatter = params => {
        const tooltipColor = color =>
          `<span class="analytic-review__container__chart__tooltip__item__color" style="background-color:${color}"></span>`
        let tooltip = `
        <div class="analytic-review__container__chart__tooltip__title">
          ${tooltipColor(params.data[5])} ${params.seriesName}
        </div>`
        this.chart.chart_review.tooltip.forEach((s, i) => {
          tooltip += `
          <div class="analytic-distribution__container__chart__tooltip__item">
            <div class="analytic-distribution__container__chart__tooltip__item--align-start">
              ${s.name[this.$i18n.locale] || s.name.default}
            </div>
            <div class="analytic-distribution__container__chart__tooltip__item--align-end">
              ${i === 0 ? params.data[1] : i === 1 ? params.data[0] : params.data[i]}
            </div>
          </div>`
        })

        return `<div class="analytic-review__container__chart__tooltip">${tooltip}</div>`
      }
    },
  },
}
</script>

<style lang="scss">
.analytic-review {
  @include box;

  &__header {
    @include header;

    &__title {
      @include title;
    }

    &__tooltip {
      margin-left: 8px;
    }
  }

  &__legend {
    padding: $spacing-sm $spacing-sm 0;

    @media (min-width: $screen-sm) {
      padding: $spacing-md $spacing-md 0;
    }
  }

  &__container {
    @include container(0);

    &__chart {
      height: 100%;

      &__tooltip {
        &__title {
          display: flex;
          align-items: center;
          margin-bottom: 4px;
          font-weight: 800;
        }

        &__item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
          font-size: $font-size-sm;
          font-weight: 400;

          &:last-child {
            margin-bottom: 0;
          }

          &--align-start {
            display: flex;
            align-items: center;
          }

          &--align-end {
            display: flex;
            align-items: flex-end;
            margin-left: 16px;
            font-weight: bold;
          }

          &__color {
            margin-right: 8px;
            border-radius: 25px;
            width: 12px;
            height: 12px;
          }
        }
      }
    }
  }
}
</style>
